/*! purgecss start ignore - PurgeCSS needs to see these classes to know to keep them in the stylesheet */
// swiper-pagination-bullet
// swiper-pagination-bullet-active
// swiper-button-disabled
// swiper-button-prev:after
// swiper-button-next:after
/*! purgecss end ignore */

import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { Swiper } from 'swiper'
import { Pagination, Navigation, A11y, Keyboard } from 'swiper/modules'
import { Swiper as SwiperCore } from 'swiper/types'
import { SwiperOptions } from 'swiper/types'
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import SliderNavigation from '@toasttab/shared/src/components/molecules/SliderNavigation/SliderNavigation'
import DynamicCards from '@toasttab/shared/src/components/molecules/CardTypes/DynamicCard'
import SliderPagination from '@toasttab/shared/src/components/molecules/SliderPagination/SliderPagination'
import { Text } from '@toasttab/shared/src/utils/craftUtils'
import { SliderProps } from './Slider.d'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import { Icon } from '@toasttab/shared/src/utils/craftUtils'

const Slider = ({
  bgColor = 'bg-white',
  centerActiveSlide,
  cssClasses,
  fullWidth,
  dataTestId = 'Slider-1',
  initSlider = true,
  mobileMargin,
  pagination,
  previousNextButtons = 'null',
  prevNextClasses,
  scrollTrackingSectionName,
  sectionDescription,
  sectionHeading,
  link,
  // Setting as true by default for now to prevent breaking changes
  // Allows us to use other components in Slider, not just cards
  sliderCards = true, // todo - default this to false
  sliderCss,
  slideCss = '',
  sliderLoop,
  slides = [],
  slidesToShow = 1,
  hideSlider,
  textAlignment = 'left',
  textAlignmentMobile = 'left',
  toggleSwitch, // visiblePrevNextSlide
  toggleSwitch2, // showPartialSlidesMobile
  uniqueId
}: SliderProps) => {
  const [swiperSlider, setSwiperSlider] = useState<SwiperCore>(null)
  const [Slides, setSlides] = useState<any[]>([])
  const [ActiveIndex, setActiveIndex] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(true)
  const ref = useRef<any | null>(null)

  const singleSlide =
    slides && slides.length === 1 && Number(slidesToShow) === 1

  const removeAttributes = (swiper: SwiperCore) => {
    if (!swiper) return
    const slides = swiper.slides
    slides.forEach((slide) => {
      slide.removeAttribute('tabindex')
      slide.removeAttribute('role')
      slide.removeAttribute('aria-label')
    })
  }

  const initSwiper = () => {
    if (!loading) {
      return
    }

    setLoading(true)
    const swiperObj: SwiperOptions = {
      init: false,
      modules: [Pagination, Navigation, A11y, Keyboard],
      speed: 300,
      threshold: 30,
      grabCursor: true,
      spaceBetween: 16,
      centeredSlides: centerActiveSlide,
      a11y: {
        enabled: true,
        slideLabelMessage: `Slide {{index}} of {{slidesLength}}`,
        scrollOnFocus: true
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true
      },
      on: {
        afterInit: function (this: SwiperCore) {
          removeAttributes(this)
        },
        init: function (this: SwiperCore) {
          removeAttributes(this)
        },
        update: function (this: SwiperCore) {
          removeAttributes(this)
        },
        resize: function (this: SwiperCore) {
          removeAttributes(this)
        }
      },
      loop: sliderLoop,
      enabled: slides?.length > 1 ? true : false,
      slidesPerView: toggleSwitch2 ? 1.25 : 1,
      observeParents: true,
      observeSlideChildren: true,
      updateOnWindowResize: true,
      breakpoints: {
        768: {
          slidesPerView: Number(slidesToShow),
          centeredSlides: false,
          spaceBetween: 24
        }
      }
    }

    if (pagination) {
      Object.assign(swiperObj, {
        pagination: {
          el: ref.current.querySelector(`.swiper-pagination`),
          clickable: false
        }
      })
    }

    if (previousNextButtons !== 'null') {
      Object.assign(swiperObj, {
        navigation: {
          nextEl: ref.current.querySelector(`.swiper-button-next`),
          prevEl: ref.current.querySelector(`.swiper-button-prev`)
        }
      })
    }

    const swiper = new Swiper(ref.current, swiperObj)

    swiper.on('init', function () {
      swiper.update()
      setActiveIndex(swiper.activeIndex)
    })

    swiper.on('slideChange', function () {
      setActiveIndex(swiper.activeIndex)
    })

    setSwiperSlider(swiper)

    setLoading(false)
  }

  useEffect(() => {
    setSlides(slides)
    if (Slides?.length > 0 && initSlider && !hideSlider) {
      initSwiper()
    }

    if (swiperSlider) {
      swiperSlider.init()
      // ref.current.addEventListener('keydown', (e: KeyboardEvent) => {
      //   console.log(e)
      //   if (e.keyCode === 9) {
      //     e.preventDefault()
      //   }
      // })
    }
    removeAttributes(swiperSlider)
  }, [Slides, swiperSlider])

  const sliderContainerClasses = classNames(
    cssClasses,
    bgColor,
    'mds-slider overflow-hidden',
    {
      'pt-[4.5rem]': !cssClasses,
      'pb-4': mobileMargin
    }
  )

  const cardTextAlign =
    Text.alignment(textAlignmentMobile) + ' sm:' + Text.alignment(textAlignment)
  const sliderClasses = classNames(
    sliderCss,
    'swiper mds-swiper mt-8 relative',
    {
      'md:mt-14': !link,
      '!overflow-visible': toggleSwitch
    }
  )

  if (slides && slides.length === 0) return null

  const containerClasses = classNames({
    'ds-container': !fullWidth,
    'pb-10 lg:pb-9': true
  })

  const sliderWrapperClasses = classNames('items-stretch', {
    'swiper-wrapper': !hideSlider,
    'md:justify-center': Slides?.length < 3 && slidesToShow !== 1,
    'mb-4': Slides?.length > Number(slidesToShow),
    'flex flex-wrap gap-[24px]': hideSlider && slidesToShow !== 1
  })

  const navigationClasses = classNames(prevNextClasses, {
    hidden: Slides?.length <= Number(slidesToShow),
    'hidden sm:flex': Slides?.length > Number(slidesToShow)
  })

  const slideClasses = classNames(
    slideCss,
    'swiper-slide !h-auto flex [&>div]:w-full [&>div]:h-full [&>a]:w-full [&>a]:h-full',
    {
      '[&:not(:last-child)]:mb-[40px] [&:not(:last-child)]:sm:mb-[96px]':
        hideSlider && slidesToShow === 1,
      'col-span-4 md:col-span-2 lg:col-span-4 md:!w-[31%]':
        hideSlider && slidesToShow === 3
    }
  )
  return (
    <section
      className={sliderContainerClasses}
      data-testid={dataTestId}
      data-tracking-section={scrollTrackingSectionName}
      id={uniqueId}
    >
      <div className='ds-wrapper overflow-hidden'>
        <div className={containerClasses}>
          <div className={cardTextAlign}>
            {sectionHeading ? (
              <Typography
                as='h2'
                classes='text-default sm:text-[2.8125rem]'
                variation='h2'
              >
                {sectionHeading}
              </Typography>
            ) : null}
            {sectionDescription ? (
              <Typography as='p' classes='mt-2 md:mt-4 text-gray-100 '>
                {sectionDescription}
              </Typography>
            ) : null}
          </div>
          {link ? (
            <div className={`${cardTextAlign} mt-8`}>
              <TextLink
                classes='!leading-[24px]'
                href={link.url}
                icon={Icon.linkIcons(link.icon)}
                text={link.label}
              />
            </div>
          ) : null}
          <div className={sliderClasses} ref={ref}>
            <div className={sliderWrapperClasses}>
              {Slides?.length > 0 &&
                Slides.map((Slide: any, i: number) => {
                  const active = ActiveIndex === i
                  const isPrevious = ActiveIndex - 1 === i
                  const isNext = ActiveIndex + Number(slidesToShow) === i
                  const slideCss = classNames(slideClasses, {
                    '[mask-image:linear-gradient(to_left,black,transparent_100px)]':
                      isPrevious && toggleSwitch && !hideSlider,
                    '[mask-image:linear-gradient(to_right,black,transparent_100px)]':
                      isNext && toggleSwitch && !hideSlider
                  })
                  return (
                    <div
                      className={slideCss}
                      data-swiper-slide-index={i + 1}
                      key={`${Slide.sectionHeading}-${i}`}
                      tabIndex={-1}
                    >
                      {sliderCards ? (
                        <DynamicCards
                          card={Slide.typeHandle}
                          {...Object.assign({}, Slide, singleSlide)}
                          slideIsActive={active}
                        />
                      ) : (
                        React.cloneElement(Slide)
                      )}
                    </div>
                  )
                })}
            </div>
            {previousNextButtons !== 'null' && !hideSlider && (
              <SliderNavigation
                classes={navigationClasses}
                previousNextButtons={previousNextButtons}
                swiper={swiperSlider}
              />
            )}
            {pagination ? <SliderPagination /> : null}
          </div>
        </div>
      </div>
    </section>
  )
}

export default Slider
